<script setup lang="ts">
/*
 * FILE: unified_ui/src/components/AppShell.vue
 * PURPOSE: Frame for every screen. Renders a header (mobile + desktop) and
 *          the navigation. On mobile, the nav is fixed to the bottom; on
 *          desktop (md+), it becomes a horizontal top tab bar.
 *          Same visual language, just placement differs.
 *
 * The root <div> is `h-full` (which only resolves to a real height because
 * index.html sets html/body/#app to height: 100%). Inside it is a flex
 * column with `<main class="flex-1 min-h-0 overflow-y-auto">` — the
 * min-h-0 is critical so that any nested flex layout (e.g. the chat) can
 * use `h-full` to fill the available main area.
 *
 * Slot: default — main content area.
 */
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useIdentityStore } from '@/stores/identity'
import BottomNav from './BottomNav.vue'
import TopNav from './TopNav.vue'

const identity = useIdentityStore()

const greeting = computed(() => {
  const name = identity.userName
  return name ? `Привет, ${name}` : 'КИБЕР Маркет'
})

const route = useRoute()
const isSettling = ref(false)
let settleTimer: number | null = null

watch(
  () => route.path,
  () => {
    isSettling.value = false
    if (settleTimer !== null) {
      clearTimeout(settleTimer)
      settleTimer = null
    }
    // requestAnimationFrame ensures the class is removed before re-added,
    // so the CSS animation re-triggers on rapid tab switches.
    requestAnimationFrame(() => {
      isSettling.value = true
      settleTimer = window.setTimeout(() => {
        isSettling.value = false
        settleTimer = null
      }, 420)
    })
  },
)

onUnmounted(() => {
  if (settleTimer !== null) clearTimeout(settleTimer)
})
</script>

<template>
  <div class="flex h-full min-h-screen flex-col bg-gradient-to-b from-[#e8f0f8] to-[#f0f4f8] dark:from-[#0b1120] dark:to-[#131c31]">
    <TopNav :greeting="greeting" />

    <!--
      LAYOUT CONTRACT — BOTTOM PILL CLEARANCE
      ----------------------------------------
      The floating bottom-nav pill sits at viewport-bottom, raised by
      env(safe-area-inset-bottom) + 12px (pb-3) + 6px (nav py-1.5) +
      ~44px (tab icon+label).  Total pill height: 68–102px above
      viewport bottom (max 102px on iPhone with home indicator).

      main pb = pill height: env(safe-area-inset-bottom) + 68px.
      This makes the content-stage bottom land EXACTLY at the pill top
      on every device.  For chat, the input form is the last item in a
      flex column → it sits at content-stage bottom → flush with the
      pill top, no gap.

      PER-VIEW CONTRACT — every view MUST add its own bottom padding
      to push scrollable content ABOVE the pill:
        OrdersView/AboutView/SupportView: pb-40 (160px) — last card
          ends 160px above the content-stage bottom, well clear of
          the pill.
        The chat surface: NO pb — the composer anchors to content-stage bottom.
      DO NOT change the per-view padding without testing in chat and
      in the other views.
    -->
    <main
      class="flex-1 min-h-0 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+68px)] md:pb-6 pt-safe"
    >
      <div class="content-stage mx-auto h-full w-full max-w-3xl px-4 py-4 md:py-6" :class="{ 'view-settle': isSettling }">
        <slot />
      </div>
    </main>

    <BottomNav />
  </div>
</template>
